import classnames from 'classnames/bind';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { Text as CustomText } from '@/_kit/text';
import { ETextSize } from '@/_kit/text/_types';
import { EVariant, TCard } from '@/_components/success-stories/_types';
import { BasketImage } from '@/_components/basket-image';
import { EAnalytics } from '@/_constants/analytics';
import { useAnalytics } from '@/_hooks/use-analytics';
import { CardObserver } from './_components/card-observer';
import { CardHeader } from './_components/card-header';
import { CardFooter } from './_components/card-footer';
import styles from './index.module.scss';

const ArticleContent = dynamic(
  () =>
    import('./_components/article-content').then((module) => ({
      default: module.ArticleContent,
    })),
  {
    ssr: false,
  },
);

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Card';

type TProps = TCard & {
  isStage: boolean;
  isIntersectingSlider: boolean;
};

export const Card = memo(function Card({
  title,
  description,
  cardType,
  variant,
  button,
  name,
  image,
  prodId,
  stageId,
  link,
  isStage,
  blockName,
  isIntersectingSlider,
}: TProps) {
  const translations = useTranslations();
  const sendEvent = useAnalytics();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleDrawerOpen = () => {
    setIsDrawerOpen(true);
  };

  const id = isStage ? stageId : prodId;

  const handleButtonClick = useCallback(() => {
    const config = {
      name: EAnalytics.SUPPLIER_LANDING_SUCCESS_STORY_T,
      params: {
        nameSubblock: blockName,
        idArticle: id,
        buttonType: translations(button),
      },
    };

    sendEvent(config);

    if (id) {
      handleDrawerOpen();
    } else if (link) {
      window.open(link, '_blank');
    }
  }, [button, id, isStage, link, translations, blockName, sendEvent]);

  const isWhite = variant === EVariant.PURPLE || variant === EVariant.PINK;

  return (
    <div
      className={cn(
        BLOCK_NAME,
        `${BLOCK_NAME}--${cardType}`,
        `${BLOCK_NAME}--${variant}`,
      )}
    >
      {image && (
        <div className={cn(`${BLOCK_NAME}__image`)}>
          <BasketImage
            alt="background"
            fill
            src={image}
            style={{
              objectFit: 'contain', // или 'contain'
              objectPosition: 'bottom right',
              borderRadius: '8px',
            }}
          />
        </div>
      )}
      <div
        className={cn(
          `${BLOCK_NAME}__inner`,
          `${BLOCK_NAME}__inner--${variant}`,
        )}
      >
        <div
          className={cn(
            `${BLOCK_NAME}__content`,
            `${BLOCK_NAME}__content--${cardType}`,
            `${BLOCK_NAME}__content--${variant}`,
          )}
        >
          <CardHeader
            cardType={cardType}
            isWhite={isWhite}
            title={title}
            variant={variant}
          />
          <CardFooter
            cardType={cardType}
            description={description}
            isWhite={isWhite}
            name={name}
          />
        </div>
        {button && (
          <div className={cn(`${BLOCK_NAME}__button-wrapper`)}>
            <button
              className={cn(
                `${BLOCK_NAME}__button`,
                `${BLOCK_NAME}__button--${variant}`,
              )}
              onClick={handleButtonClick}
              type="button"
            >
              <CustomText
                size={ETextSize.MEDIUM}
                text={translations(button)}
                variant="body"
              />
            </button>
          </div>
        )}
      </div>
      <div className={cn(`${BLOCK_NAME}__observer`)}>
        <CardObserver
          blockName={blockName}
          isIntersectingSlider={isIntersectingSlider}
          slideId={id}
        />
      </div>
      {id && (
        <ArticleContent
          articleId={id}
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
        />
      )}
    </div>
  );
});
